import React, { useEffect, useRef, useCallback } from 'react';
import './JitsiJoinModal.css';

/**
 * JitsiJoinModal - In-app video call modal using Jitsi Meet's external_api.js iframe.
 *
 * Embeds Jitsi's web client in an iframe with the given room name and display name.
 * The room name should match OnlineClass.jitsiRoomName for cross-platform consistency.
 *
 * Props:
 * - isOpen: boolean - controls modal visibility
 * - roomName: string - the Jitsi room name (e.g., 'kn-edu-portal-abc123')
 * - displayName: string - the participant's display name in the call
 * - onClose: function - called when the user closes the modal
 * - config: object (optional) - additional Jitsi config options
 */
function JitsiJoinModal({ isOpen, roomName, displayName, onClose, config = {} }) {
  const iframeRef = useRef(null);
  const apiRef = useRef(null);

  const handleClose = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.dispose();
      apiRef.current = null;
    }
    onClose();
  }, [onClose]);

  const handleParticipantLeft = useCallback((participant) => {
    console.log('Participant left:', participant);
  }, []);

  const handleVideoConferenceJoined = useCallback((participant) => {
    console.log('Joined video conference:', participant);
  }, []);

  const handleVideoConferenceLeft = useCallback(() => {
    console.log('Left video conference');
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    if (!isOpen || !roomName || !iframeRef.current) return;

    // Load Jitsi external API script dynamically
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    script.onload = () => {
      if (window.JitsiMeetExternalAPI && iframeRef.current) {
        const domain = 'meet.jit.si';
        const options = {
          roomName: roomName,
          width: '100%',
          height: '100%',
          parentNode: iframeRef.current,
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            disableInviteMoreFuncs: true,
            requireDisplayName: false,
            lobby: { enabled: false },
            securityUiDisabled: true,
          },
          interfaceConfigOverwrite: {
            DEFAULT_BACKGROUND: '#f9f9f9',
            // Show only essential toolbar buttons
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
              'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
              'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
              'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone', 'security'
            ],
            // Hide redundant buttons for a cleaner classroom experience
            SETTINGS_SECTIONS: ['devices', 'language', 'moderator'],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            DEFAULT_LOGO_URL: '',
            ...config.interfaceConfigOverwrite,
          },
          userInfo: {
            displayName: displayName,
          },
          ...config,
        };

        const api = new window.JitsiMeetExternalAPI(domain, options);
        apiRef.current = api;

        // Handle participant events (optional analytics)
        api.addEventListeners({
          readyToClose: handleClose,
          participantLeft: handleParticipantLeft,
          videoConferenceJoined: handleVideoConferenceJoined,
          videoConferenceLeft: handleVideoConferenceLeft,
        });
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [isOpen, roomName, displayName, config, handleClose, handleParticipantLeft, handleVideoConferenceJoined, handleVideoConferenceLeft]);

  if (!isOpen) return null;

  return (
    <div className="jitsi-modal-overlay">
      <div className="jitsi-modal-content">
        <div className="jitsi-modal-header">
          <h3>Online Class</h3>
          <button className="jitsi-close-button" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="jitsi-iframe-container" ref={iframeRef} />
      </div>
    </div>
  );
}

export default JitsiJoinModal;
